import {Component, Input, OnInit} from '@angular/core';
import {FormArray, FormBuilder, FormGroup, Validators} from "@angular/forms";
import {NgbActiveModal} from "@ng-bootstrap/ng-bootstrap";
import {MessagesService} from "../../services/messages/messages.service";
import {CategoryService} from "../../services/category/category.service";
import {ArtworkService} from "../../services/artwork/artwork.service";
import {Category} from "../../models/category";
import {merge} from "rxjs";
import {ArtworkCategory} from "../../models/artwork-category";
import {Media} from "../../models/media";
import {Paragraph} from "../../models/artwork-category-paragraph";
import {Artwork} from "../../models/artwork";
import {DatabaseService} from "../../services/database/database.service";

@Component({
  selector: 'app-artwork-category-modal',
  templateUrl: './artwork-category-modal.component.html',
  styleUrls: ['./artwork-category-modal.component.scss']
})
export class ArtworkCategoryModal implements OnInit {
  @Input() mediaImages: Array<Media>;
  @Input() mediaVideos: Array<Media>;
  @Input() mediaAudios: Array<Media>;
  @Input() categories: Array<Category>;
  @Input() assignedCategories: Array<Category>;
  @Input() selectedArtworkCategory: ArtworkCategory;
  @Input() projectId: string;
  @Input() databaseId: string;
  @Input() artworkId: string;
  @Input() code: string;

  artworkCategoryForm: FormGroup;
  artworkCategoryIsSubmitted: boolean = false;

  selectedImages = [];
  selectedAudios = [];
  selectedVideos = [];

  paragraphs: FormArray;

  assignableCategories: Array<Category> = [];

  selectedCategoryName: string;

  constructor(
    public activeModal: NgbActiveModal,
    private formBuilder: FormBuilder,
    private artworkService: ArtworkService,
    private categoryService: CategoryService,
    private messagesService: MessagesService,
    private databaseService: DatabaseService,
  ) {
  }

  ngOnInit() {
    merge(
      this.categoryService.getAllMyCategories(this.projectId, this.databaseId),
      this.categoryService.onChange
    ).subscribe(
      () => {
        if (!this.selectedArtworkCategory) {
          this.assignableCategories = this.categoryService.categories.filter(c => !this.assignedCategories.find(cat => cat._id === c._id))
        }
      },
      err => this.messagesService.errorModal(err)
    );

    this.artworkCategoryForm = this.formBuilder.group({
      paragraphs: this.formBuilder.array([this.initParagraphs()]),
      images: [''],
      videos: [''],
      audios: [''],
      category_id: ['', [Validators.required]]
    });

    if (this.selectedArtworkCategory) {
      this.artworkCategoryForm.patchValue({
        'images': this.selectedArtworkCategory.images.map(i => i['_id']),
        'audios': this.selectedArtworkCategory.audios.map(i => i['_id']),
        'videos': this.selectedArtworkCategory.videos.map(i => i['_id']),
        'category_id': this.selectedArtworkCategory._id
      });

      this.selectedCategoryName = this.categories.find(c => c._id == this.selectedArtworkCategory._id).name;
      this.artworkCategoryForm.controls['category_id'].disable();

      this.artworkCategoryForm.setControl('paragraphs', this.formBuilder.array(this.selectedArtworkCategory.paragraphs.map(p => this.initParagraphs(p)) || []));

      this.selectedImages = this.selectedArtworkCategory.images;
      this.selectedAudios = this.selectedArtworkCategory.audios;
      this.selectedVideos = this.selectedArtworkCategory.videos;
    }
  }

  initParagraphs(paragraph?: Paragraph) {
    return this.formBuilder.group({
      _id: [paragraph ? paragraph._id : null],
      title: [paragraph ? paragraph.title : ''],
      content: [paragraph ? paragraph.content: null, [Validators.required]],
    });
  }

  onCategorySubmit() {
    this.artworkCategoryIsSubmitted = true;
    if (this.artworkCategoryForm.invalid) {
      return;
    }

    const payload = this.artworkCategoryForm.getRawValue();
    payload['paragraphs'] = payload['paragraphs'].filter(p => !!p.content.trim()).map(p => new Paragraph(p));

    this.artworkService.addCategory(this.projectId, this.databaseId, this.artworkId, payload.category_id, new ArtworkCategory(payload))
      .subscribe(
        () => this.messagesService.successModal('Artwork updated!', 'Done!'),
        err => this.messagesService.errorModal(err.toString()),
        () => this.activeModal.close(true)
      )
    ;
  }

  private _onSelectMedia(key: string, media: Media) {
    const resources = this.artworkCategoryForm.getRawValue()[key] || [];

    const index = resources.findIndex(id => id === media._id);

    if (index === -1) {
      resources.push(media._id);

      const patchValue = {};
      patchValue[key] = resources;

      this.artworkCategoryForm.patchValue(patchValue);
    }
  }

  private _onRemoveMedia(key: string, media: Media) {
    const resources = this.artworkCategoryForm.getRawValue()[key] || [];

    const index = resources.findIndex(id => id === media._id);

    if (index !== -1) {
      resources.splice(index, 1);

      this.artworkCategoryForm.patchValue({
        keys: resources
      });
    }
  }

  onSelectImage(media: Media) {
    this._onSelectMedia('images', media);
  }

  onSelectVideo(media: Media) {
    this._onSelectMedia('videos', media);
  }

  onSelectAudio(media: Media) {
    this._onSelectMedia('audios', media);
  }

  onRemoveImage(media: Media) {
    this._onRemoveMedia('images', media)
  }

  onRemoveVideo(media: Media) {
    this._onRemoveMedia('videos', media)
  }

  onRemoveAudio(media: Media) {
    this._onRemoveMedia('audios', media)
  }

  onAddParagraph() {
    this.paragraphs = this.artworkCategoryForm.get('paragraphs') as FormArray;
    this.paragraphs.push(this.initParagraphs());
  }

  onRemoveParagraph(index: number) {
    const control = <FormArray>this.artworkCategoryForm.controls['paragraphs'];
    control.removeAt(index);
  }
}
